import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DEFAULT_PRIVACY = `## Who we are
Elevation650 ("we", "us") operates The Self-Agency Project, offering in-person self-defense training and online courses in Alberta, Canada. We comply with Alberta's Personal Information Protection Act (PIPA) and Canada's PIPEDA, and we support school boards' obligations under Alberta's Protection of Privacy Act (POPA, formerly FOIP).
Our designated Privacy Officer is Andrew Kiedyk — admin@elevation650.com — and can answer any question about how personal information is collected, used, stored, or disclosed, including information handled by service providers outside Canada.

## What we collect
We collect only what we need to run the business:
- Contact details — name, email and phone when you request a booking, sign a waiver, register for a course, or buy training.
- Account & learning data — course progress, quiz results, and rewards if you use our learning app.
- Payment data — payments are processed by Stripe. We never see or store your full card number.
- Messages — texts and emails exchanged with us are kept so we have a record of our conversation.
- Waivers & signed agreements — kept as legal records.

## How we use it
- Scheduling and confirming your sessions (including SMS/email confirmations and reminders).
- Delivering course access, tracking progress, and issuing certificates.
- Processing payments and receipts.
- Responding to your questions.
We never sell or rent personal information. We never use student information for advertising, profiling, product development unrelated to the service, or AI training.

## Schools & student information
When a school or school board purchases seats, student information (name, email, course progress) is collected and used on behalf of, and under the authority of, that school board as permitted by Alberta's Protection of Privacy Act (POPA). The board remains in control of its students' information; we act as its service provider. We use student information only to deliver the training the board purchased — nothing else. We notify boards of any privacy incident affecting their students without unreasonable delay (target: within 72 hours), restrict access to authorized staff only, and return or securely destroy student data at the end of the agreement or on the board's request. Parent/guardian consent for student participation is managed by the school under its own policies. See our Privacy & Schools page for a full compliance summary.

## Service providers outside Canada
As required by PIPA sections 6 and 13.1, we disclose that we use the following service providers, some of which collect, use, or store personal information outside Canada (United States):
- Stripe (United States) — payment processing.
- Twilio (United States) — sending and receiving text messages.
- Resend (United States) — sending transactional email.
- Google (United States) — business calendar scheduling only (see below).
- Secure cloud hosting (United States) — hosting of the app and its encrypted database.
Each provider receives only the minimum information needed to perform its function and is not permitted to use it for any other purpose. Questions about our use of foreign service providers can be directed to our Privacy Officer: admin@elevation650.com.

## Google Calendar
Our booking system connects to our own Google Calendar (via the Google Calendar API) so confirmed sessions appear on our business schedule and busy times are blocked from booking. This connection accesses only our business calendar — never yours. Use of information received from Google APIs adheres to the Google API Services User Data Policy ( https://developers.google.com/terms/api-services-user-data-policy ), including the Limited Use requirements.

## Texts & email
We send booking confirmations, reminders, and replies to conversations you start. Promotional messages (such as discount codes) are sent only to existing customers or people who asked for them, in line with Canada's Anti-Spam Legislation (CASL); every such text identifies us and you can reply STOP to opt out at any time.

## How long we keep it
- Account and learning data — while your account is active; deleted within 60 days of a verified deletion request or the end of a school agreement.
- Bookings and message history — up to 2 years after our last interaction.
- Signed waivers and agreements — retained as legal records for the limitation periods required by law.
- Financial records — 7 years, as required by Canadian tax law.

## How we protect it
All traffic is encrypted in transit (HTTPS/TLS). Passwords are stored only as salted bcrypt hashes. Access is role-based (admins, teachers, and students each see only what they need), administrative actions on sensitive data are logged, and the database is access-controlled and not publicly reachable.

## If something goes wrong
If a privacy breach creates a real risk of significant harm, we will notify the Office of the Information and Privacy Commissioner of Alberta (OIPC) without unreasonable delay as required by PIPA, notify affected individuals, and notify any affected school board (target: within 72 hours of discovery). Our Privacy Officer coordinates containment, assessment, and remediation.

## Your rights
You may request a copy of your personal information, ask us to correct it, or ask us to delete it (subject to legal retention requirements like signed waivers and tax records). Email admin@elevation650.com and we'll respond within 45 days as required by PIPA.

## Children
Youth programs are registered and managed by a parent, guardian, or school. We collect student information only as provided by them for training purposes.

## Changes
If we change this policy we'll update this page and the date above.`;

export const DEFAULT_TERMS = `## The basics
By booking training, buying a course, or using The Self-Agency Project app on selfagency.ca, you agree to these terms. If you don't agree, please don't use the services.

## Training & assumption of risk
Self-defense training is physical. In-person participation requires a signed liability waiver, and you (or your guardian) accept the inherent risks of physical training. Follow instructor directions at all times.

## Bookings, packs & payments
- Booking requests are confirmed by us before they're final.
- Session packs are tracked per person and are non-transferable unless we agree otherwise.
- Payments are processed securely by Stripe. Prices are in CAD unless stated otherwise.
- Need to cancel or reschedule? Give us as much notice as you can — repeated no-shows may forfeit a pack session.

## Online courses & accounts
- Course access is for the purchaser (or the registered student) only — don't share accounts or access codes.
- Course content, videos and materials are our intellectual property. No copying, redistribution or resale.
- We may suspend accounts that abuse the platform or other members.

## No professional guarantee
Training improves skills and awareness but no outcome is guaranteed in a real-world encounter. Content is educational and not legal advice.

## Liability
To the maximum extent permitted by law, our liability for any claim related to the services is limited to the amount you paid us for the service giving rise to the claim.

## Contact
Questions about these terms: info@elevation650.com — these terms are governed by the laws of Alberta, Canada.`;

const linkify = (t) =>
  t.split(/(\s+)/).map((w, i) => {
    if (/^https?:\/\//.test(w)) return <a key={i} href={w} className="text-[#FFE01A]" target="_blank" rel="noreferrer">{w.replace(/^https?:\/\//, "")}</a>;
    if (/^[\w.+-]+@[\w-]+\.[\w.]+$/.test(w)) return <a key={i} href={`mailto:${w}`} className="text-[#FFE01A]">{w}</a>;
    return w;
  });

function LegalBody({ text }) {
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push({ type: "ul", items: list }); list = null; } };
  for (const raw of (text || "").split("\n")) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    if (line.startsWith("## ")) { flush(); blocks.push({ type: "h2", text: line.slice(3) }); }
    else if (line.startsWith("- ")) { list = list || []; list.push(line.slice(2)); }
    else { flush(); blocks.push({ type: "p", text: line }); }
  }
  flush();
  return (
    <div>
      {blocks.map((b, i) =>
        b.type === "h2" ? (
          <h2 key={i} className="text-lg font-bold font-display mt-8 mb-2" style={{ color: "#fff" }}>{b.text}</h2>
        ) : b.type === "ul" ? (
          <ul key={i} className="list-disc pl-5 space-y-1 text-[#D4D4D8] text-sm leading-relaxed mb-2">
            {b.items.map((it, j) => <li key={j}>{linkify(it)}</li>)}
          </ul>
        ) : (
          <p key={i} className="text-[#D4D4D8] text-sm leading-relaxed mb-2">{linkify(b.text)}</p>
        )
      )}
    </div>
  );
}

function Shell({ title, updated, text, testid }) {
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-5 py-14" data-testid={testid}>
        <Link to="/" className="text-[#FFE01A] text-sm font-bold uppercase tracking-wide no-underline">&larr; selfagency.ca</Link>
        <h1 className="text-4xl sm:text-5xl font-extrabold font-display mt-4 mb-1" style={{ color: "#fff" }}>{title}</h1>
        <p className="text-[#A1A1AA] text-xs mb-8">Last updated: {updated}</p>
        <LegalBody text={text} />
        <div className="border-t border-white/10 pt-6 mt-10 text-xs text-[#A1A1AA]">
          © {new Date().getFullYear()} Elevation650 · <Link to="/privacy" className="text-[#A1A1AA]">Privacy Policy</Link> · <Link to="/terms" className="text-[#A1A1AA]">Terms of Service</Link> · <Link to="/privacy-compliance" className="text-[#A1A1AA]">Privacy & Schools</Link>
        </div>
      </div>
    </div>
  );
}

const useLegal = () => {
  const [legal, setLegal] = useState(null);
  useEffect(() => {
    axios.get(`${API}/site/content`).then(({ data }) => setLegal(data.legal || {})).catch(() => setLegal({}));
  }, []);
  return legal;
};

export default function PrivacyPage() {
  const legal = useLegal();
  if (legal === null) return <div className="min-h-screen bg-black" />;
  return <Shell title="Privacy Policy" testid="privacy-page"
    updated={legal.updated || "June 2026"} text={(legal.privacy || "").trim() || DEFAULT_PRIVACY} />;
}

export function TermsPage() {
  const legal = useLegal();
  if (legal === null) return <div className="min-h-screen bg-black" />;
  return <Shell title="Terms of Service" testid="terms-page"
    updated={legal.updated || "June 2026"} text={(legal.terms || "").trim() || DEFAULT_TERMS} />;
}
